/**
 * CRMRenewalsPage — Pipeline rinnovi polizze in scadenza (CRM-2, feb 2026).
 * 4 viste: Tutti / T-30 / T-15 / T-7 (badge count). Drawer dettaglio rinnovo.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const WINDOWS = [
  { key: "all", label: "Tutti", color: "bg-zinc-100 text-zinc-700" },
  { key: "t30", label: "T-30", color: "bg-sky-100 text-sky-700" },
  { key: "t15", label: "T-15", color: "bg-amber-100 text-amber-700" },
  { key: "t7", label: "T-7", color: "bg-rose-100 text-rose-700" },
  { key: "scaduti", label: "Scaduti", color: "bg-zinc-200 text-zinc-700" },
];

const STATI = ["pending", "in_lavorazione", "preventivo_inviato", "confermato", "perso"];

export default function CRMRenewalsPage() {
  const [window, setWindow] = useState("all");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [scanRunning, setScanRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/crm/renewals?window=${window}`, { withCredentials: true });
      setItems(r.data.items || []);
      setSummary(r.data.summary || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [window]);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    setScanRunning(true);
    try {
      const r = await axios.post(`${API}/api/admin/crm/renewals/scan`, {}, { withCredentials: true });
      alert(`Scan completato: ${r.data.created_renewals} nuovi rinnovi · ${r.data.created_tasks} task generati`);
      load();
    } catch (e) { alert("Errore scan"); }
    finally { setScanRunning(false); }
  };

  return (
    <div className="p-4 md:p-6" data-testid="crm-renewals-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><RefreshCw className="w-5 h-5" /> Rinnovi polizze</h2>
          <p className="text-xs text-zinc-500">Daily scan auto @ 06:00 · alerts T-30/T-15/T-7 con priorità task</p>
        </div>
        <div className="flex items-center gap-2">
        <a href={`${API}/api/admin/crm/export/renewals?format=xlsx`} target="_blank" rel="noreferrer"
           data-testid="crm-renewals-export-btn"
           className="px-3 py-2 text-xs rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Export</a>
        <button onClick={runScan} disabled={scanRunning} data-testid="crm-renewals-scan-btn"
          className="px-3 py-2 text-sm bg-zinc-900 text-white rounded hover:bg-zinc-800 flex items-center gap-1 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${scanRunning ? "animate-spin" : ""}`} /> Scan ora
        </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 border-b border-zinc-200 pb-2">
        {WINDOWS.map((w) => (
          <button key={w.key} onClick={() => setWindow(w.key)}
            className={`px-3 py-1.5 text-xs rounded-full ${window === w.key ? "bg-zinc-900 text-white" : w.color}`}
            data-testid={`crm-renewals-window-${w.key}`}>
            {w.label} <span className="ml-1 opacity-70">({summary[w.key] || 0})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="text-center py-8 text-zinc-400 italic">Nessun rinnovo nella finestra</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full text-xs md:text-sm" data-testid="crm-renewals-table">
              <thead className="border-b border-zinc-200 text-left text-zinc-500">
                <tr>
                  <th className="py-2 pr-2">Cliente</th><th>N° polizza</th><th>Compagnia</th>
                  <th>Scadenza</th><th>Giorni</th><th>Premio</th><th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} onClick={() => setSelectedId(r.id)}
                      className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                      data-testid={`crm-renewal-row-${r.id}`}>
                    <td className="py-2 pr-2">{r.customer_label}</td>
                    <td className="font-mono">{r.policy_numero}</td>
                    <td>{r.policy_compagnia}</td>
                    <td>{r.data_scadenza?.slice(0, 10)}</td>
                    <td><DaysBadge n={r.giorni_alla_scadenza} /></td>
                    <td>€ {Number(r.policy_premio_attuale_eur || 0).toFixed(2)}</td>
                    <td><StatoBadge stato={r.stato_rinnovo} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {items.map((r) => (
              <div key={r.id} onClick={() => setSelectedId(r.id)}
                   className="border border-zinc-200 rounded p-3 hover:bg-zinc-50 cursor-pointer"
                   data-testid={`crm-renewal-card-${r.id}`}>
                <p className="font-semibold text-sm">{r.customer_label}</p>
                <p className="text-xs font-mono text-zinc-500">{r.policy_numero} · {r.policy_compagnia}</p>
                <p className="text-xs mt-1 flex justify-between">
                  <span>Scade {r.data_scadenza?.slice(0, 10)}</span>
                  <DaysBadge n={r.giorni_alla_scadenza} />
                </p>
                <p className="text-xs mt-1"><StatoBadge stato={r.stato_rinnovo} /></p>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedId && <RenewalDrawer id={selectedId} onClose={() => { setSelectedId(null); load(); }} />}
    </div>
  );
}

function DaysBadge({ n }) {
  if (n === null || n === undefined) return <span className="text-zinc-400">—</span>;
  if (n < 0) return <span className="text-rose-700 font-semibold">scaduta {Math.abs(n)}g fa</span>;
  if (n <= 7) return <span className="text-rose-600 font-semibold">{n}g</span>;
  if (n <= 15) return <span className="text-amber-600">{n}g</span>;
  return <span className="text-sky-600">{n}g</span>;
}

function StatoBadge({ stato }) {
  const map = {
    pending: "bg-zinc-100 text-zinc-700",
    in_lavorazione: "bg-sky-100 text-sky-700",
    preventivo_inviato: "bg-violet-100 text-violet-700",
    confermato: "bg-emerald-100 text-emerald-700",
    perso: "bg-rose-100 text-rose-700",
  };
  return <span className={`px-1.5 py-0.5 rounded text-[10px] ${map[stato] || "bg-zinc-100"}`}>{stato}</span>;
}

function RenewalDrawer({ id, onClose }) {
  const [data, setData] = useState(null);
  const [stato, setStato] = useState("");
  const [premio, setPremio] = useState("");
  const [nota, setNota] = useState("");
  const [taskCreated, setTaskCreated] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/crm/renewals/${id}`, { withCredentials: true }).then((r) => {
      setData(r.data.renewal);
      setStato(r.data.renewal.stato_rinnovo || "pending");
      setPremio(r.data.renewal.premio_proposto_eur || "");
    });
  }, [id]);

  const save = async () => {
    const body = { stato_rinnovo: stato };
    if (premio) body.premio_proposto_eur = parseFloat(premio);
    if (nota) body.nota = nota;
    await axios.patch(`${API}/api/admin/crm/renewals/${id}`, body, { withCredentials: true });
    onClose();
  };

  const genTask = async () => {
    const r = await axios.post(`${API}/api/admin/crm/renewals/${id}/genera-task`, {}, { withCredentials: true });
    setTaskCreated(r.data);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center md:justify-end" onClick={onClose} data-testid="crm-renewal-drawer">
      <div className="bg-white w-full md:max-w-lg md:h-full overflow-y-auto rounded-t-2xl md:rounded-t-none md:rounded-l-2xl p-5" onClick={(e) => e.stopPropagation()}>
        {!data ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-amber-500" /> Rinnovo</h3>
            <p className="text-xs text-zinc-500 mb-3">Scade {data.data_scadenza?.slice(0, 10)}</p>

            <div className="space-y-2 text-sm">
              <label className="block">
                <span className="text-xs text-zinc-500">Stato rinnovo</span>
                <select value={stato} onChange={(e) => setStato(e.target.value)}
                  className="w-full mt-1 px-3 py-2 text-sm rounded border border-zinc-200"
                  data-testid="crm-renewal-stato-select">
                  {STATI.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-zinc-500">Premio proposto €</span>
                <input type="number" step="0.01" value={premio} onChange={(e) => setPremio(e.target.value)}
                  className="w-full mt-1 px-3 py-2 text-sm rounded border border-zinc-200" />
              </label>
              <label className="block">
                <span className="text-xs text-zinc-500">Nota</span>
                <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={2}
                  className="w-full mt-1 px-3 py-2 text-sm rounded border border-zinc-200" />
              </label>
            </div>

            {data.task_operatore_id ? (
              <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Task collegato: {data.task_operatore_id.slice(0, 8)}</p>
            ) : taskCreated ? (
              <p className="text-xs text-emerald-700 mt-3">✓ Task creato (priorità {taskCreated.priorita})</p>
            ) : (
              <button onClick={genTask} className="mt-3 text-xs underline" data-testid="crm-renewal-gen-task-btn">Crea task operatore</button>
            )}

            <div className="mt-4 flex gap-2">
              <button onClick={onClose} className="flex-1 py-2 text-sm rounded bg-zinc-100">Annulla</button>
              <button onClick={save} className="flex-1 py-2 text-sm rounded bg-zinc-900 text-white" data-testid="crm-renewal-save-btn">Salva</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
